import { useRef, useState } from "react";
import { Loader2, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { fmtDate, inputCls } from "@/lib/labels";

const status = (item) => (item.approved_at ? `✓ العميل وافق ${fmtDate(item.approved_at)}` : "مستني موافقة العميل");

const UploadBtn = ({ label, accept, onFile, testId, busy }) => {
  const ref = useRef(null);
  return (
    <>
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-white disabled:opacity-50" data-testid={testId}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {label}
      </button>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} data-testid={`${testId}-input`} />
    </>
  );
};

export const OrderMedia = ({ o, i, onChange }) => {
  const [text, setText] = useState(o.script?.text || "");
  const [drive, setDrive] = useState(o.delivery_url || "");
  const [busy, setBusy] = useState("");
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { onChange((await fn()).data); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const upload = (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(kind, () => api.post(`/admin/orders/${o.id}/${kind}`, fd), "الفويس أوفر اتبعت للعميل");
  };
  return (
    <details className="rounded-2xl bg-black/30 p-3 text-sm" data-testid={`admin-order-media-${i}`}>
      <summary className="cursor-pointer font-bold text-[var(--km-lilac)]">الاسكربت والفويس والفيديو</summary>
      <div className="mt-3 space-y-4">
        <div>
          <p className="mb-1 font-bold text-white">الاسكربت {o.script && <span className="text-xs text-purple-300/70">نسخة {o.script.version} · {status(o.script)}</span>}</p>
          <textarea className={`${inputCls} min-h-[120px]`} value={text} onChange={(e) => setText(e.target.value)} maxLength={20000} data-testid={`admin-script-input-${i}`} />
          <button type="button" disabled={busy === "script" || text.trim().length < 3} onClick={() => run("script", () => api.put(`/admin/orders/${o.id}/script`, { text }), "الاسكربت اتبعت للعميل")}
            className="mt-2 inline-flex items-center gap-1 rounded-full bg-[var(--km-pink)] px-4 py-2 font-bold text-white disabled:opacity-50" data-testid={`admin-script-send-${i}`}>
            {busy === "script" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 -scale-x-100" />} {o.script ? "ابعت نسخة معدّلة" : "ابعت الاسكربت"}
          </button>
        </div>
        <div className="space-y-2">
          <p className="font-bold text-white">الفويس أوفر {o.voice && <span className="text-xs text-purple-300/70">نسخة {o.voice.version} · {status(o.voice)}</span>}</p>
          {o.voice && <audio controls preload="none" className="w-full" src={`${API}/admin/orders/${o.id}/voice?v=${o.voice.version}`} />}
          <UploadBtn label={o.voice ? "ارفع نسخة جديدة" : "ارفع الفويس أوفر"} accept=".mp3,.wav,.m4a,.ogg" busy={busy === "voice"} onFile={(f) => upload("voice", f)} testId={`admin-voice-upload-${i}`} />
        </div>
        <div className="space-y-2">
          <p className="font-bold text-white">لينك الفيديو النهائي (جوجل درايف)</p>
          <input dir="ltr" className={`${inputCls} text-left`} placeholder="https://drive.google.com/..." value={drive} onChange={(e) => setDrive(e.target.value)} data-testid={`admin-delivery-input-${i}`} />
          <p className="text-xs text-purple-300/60">العميل هيشوف زرار «استلام الفيديو» لما الأوردر يوصل «تم الاستلام». اتأكد إن اللينك متاح للي معاه اللينك. موافقات العميل مش شرط لتحريك المراحل.</p>
          <button type="button" disabled={busy === "delivery" || drive.trim() === (o.delivery_url || "")} onClick={() => run("delivery", () => api.put(`/admin/orders/${o.id}/delivery`, { url: drive.trim() }), drive.trim() ? "لينك الفيديو اتحفظ" : "اللينك اتشال")}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--km-pink)] px-4 py-2 font-bold text-white disabled:opacity-50" data-testid={`admin-delivery-save-${i}`}>
            {busy === "delivery" && <Loader2 className="h-4 w-4 animate-spin" />} حفظ اللينك
          </button>
        </div>
      </div>
    </details>
  );
};

export const AdminSupport = () => {
  const [phone, setPhone] = useState("");
  const [current, setCurrent] = useState("");
  const [busy, setBusy] = useState(false);
  useState(() => { api.get("/settings/public").then((r) => setCurrent(r.data.support_whatsapp)).catch(() => {}); });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setCurrent((await api.put("/admin/settings/support", { phone })).data.support_whatsapp); setPhone(""); toast.success("رقم الواتساب اتحدث في كل الموقع"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-6 max-w-lg space-y-3 rounded-3xl border border-purple-500/25 bg-[var(--km-card)] p-6" data-testid="admin-support-form">
      <h2 className="font-display text-3xl text-white">رقم واتساب الدعم</h2>
      <p className="text-sm text-purple-300/70">الرقم الحالي: <span dir="ltr" data-testid="admin-support-current">+{current}</span>. بيتستخدم في كل زراير الواتساب وزرار «كلم الدعم». مش بيغيّر رقم استلام الفلوس.</p>
      <input dir="ltr" className={`${inputCls} text-right`} placeholder="+20 10 0000 0000" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="admin-support-input" />
      <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[var(--km-pink)] px-5 py-2.5 font-bold text-white" data-testid="admin-support-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ الرقم</button>
    </form>
  );
};
